<template>
  <div class="flex flex-col" :class="horizontal ? 'w-full sm:flex-row sm:items-center gap-4' : 'gap-2'">
    <!-- Platform filter -->
    <Menu as="div" class="relative inline-block text-left w-full lg:w-auto" :class="{ 'sm:flex-1 lg:flex-none': horizontal }">
      <MenuButton :class="{ 'lg:w-[240px]': !horizontal }" class="relative w-full disabled:bg-gray-400 inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700">
        <div class="flex-1 text-left font-light flex gap-1">
          <span class="text-text-secondary">{{ $t('coach.filters.platform') }}</span>
          <span v-if="platform">{{ platformLabel }}</span>
          <span v-else>{{ $t('common.all') }}</span>
        </div>
        <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
      </MenuButton>
      <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
        <MenuItems class="absolute right-0 z-50 mt-2 w-full lg:w-[192px] origin-top-right rounded-md bg-gray-800 border border-gray-700 shadow-lg focus:outline-none">
          <div class="py-1 flex flex-col px-4">
            <MenuItem v-for="opt in platformOptions" :key="opt.value" @click.prevent="$emit('update:platform', opt.value)">
              <a href="#" :class="[platform === opt.value ? 'text-text-primary' : 'text-text-secondary', 'group flex items-center py-2 text-sm w-full justify-start font-light']">
                {{ opt.label }}
                <CheckIcon v-if="platform === opt.value" class="h-5 w-5 ml-auto text-primary-400" />
              </a>
            </MenuItem>
          </div>
        </MenuItems>
      </transition>
    </Menu>

    <!-- Match type filter -->
    <Menu as="div" class="relative inline-block text-left w-full lg:w-auto" :class="{ 'sm:flex-1 lg:flex-none': horizontal }">
      <MenuButton :class="{ 'lg:w-[240px]': !horizontal }" class="relative w-full disabled:bg-gray-400 inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700">
        <div class="flex-1 text-left font-light flex gap-1">
          <span class="text-text-secondary">{{ $t('coach.filters.match_type') }}</span>
          <span v-if="matchType">{{ matchType }}</span>
          <span v-else>{{ $t('common.all') }}</span>
        </div>
        <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
      </MenuButton>
      <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
        <MenuItems class="absolute right-0 z-50 mt-2 w-full lg:w-[192px] origin-top-right rounded-md bg-gray-800 border border-gray-700 shadow-lg focus:outline-none">
          <div class="py-1 flex flex-col px-4">
            <MenuItem @click.prevent="$emit('update:matchType', '')">
              <a href="#" :class="[!matchType ? 'text-text-primary' : 'text-text-secondary', 'group flex items-center py-2 text-sm w-full justify-start font-light']">
                {{ $t('coach.filters.all_match_types') }}
                <CheckIcon v-if="!matchType" class="h-5 w-5 ml-auto text-primary-400" />
              </a>
            </MenuItem>
            <MenuItem v-for="type in matchTypes" :key="type" @click.prevent="$emit('update:matchType', type)">
              <a href="#" :class="[type === matchType ? 'text-text-primary' : 'text-text-secondary', 'group flex items-center py-2 text-sm w-full justify-start']">
                {{ type }}
                <CheckIcon v-if="type === matchType" class="h-5 w-5 ml-auto text-primary-400" />
              </a>
            </MenuItem>
          </div>
        </MenuItems>
      </transition>
    </Menu>

    <!-- Map filter -->
    <Menu as="div" class="relative inline-block text-left w-full lg:w-auto" :class="{ 'sm:flex-1 lg:flex-none': horizontal }">
      <MenuButton :class="{ 'lg:w-[240px]': !horizontal }" class="relative w-full disabled:bg-gray-400 inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700">
        <div class="flex-1 text-left font-light flex gap-1">
          <span class="text-text-secondary">{{ $t('coach.filters.map') }}</span>
          <span v-if="map">{{ map.friendly_name }}</span>
          <span v-else>{{ $t('common.all') }}</span>
        </div>
        <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
      </MenuButton>
      <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
        <MenuItems class="absolute right-0 z-50 mt-2 w-full lg:w-[240px] origin-top-right rounded-md bg-gray-800 border border-gray-700 shadow-lg focus:outline-none">
          <div class="py-1 flex flex-col px-4">
            <MenuItem @click.prevent="$emit('update:map', '')">
              <a href="#" :class="[!map ? 'text-text-primary' : 'text-text-secondary', 'group flex items-center py-2 text-sm w-full justify-start font-light']">
                {{ $t('coach.filters.all_maps') }}
                <CheckIcon v-if="!map" class="h-5 w-5 ml-auto text-primary-400" />
              </a>
            </MenuItem>
            <MenuItem v-for="m in maps" :key="m.name" @click.prevent="$emit('update:map', m)">
              <a href="#" :class="[m.name === map?.name ? 'text-text-primary' : 'text-text-secondary', 'group flex items-center py-2 text-sm w-full justify-start']">
                {{ m.friendly_name }}
                <CheckIcon v-if="m.name === map?.name" class="h-5 w-5 ml-auto text-primary-400" />
              </a>
            </MenuItem>
          </div>
        </MenuItems>
      </transition>
    </Menu>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Menu, MenuButton, MenuItems, MenuItem,
} from '@headlessui/vue';
import { ChevronDownIcon, CheckIcon } from '@heroicons/vue/24/outline';
import { platformFilterDisplayLabel } from '../../../utils/matchPlatform.js';

const { t } = useI18n();

const props = defineProps({
  platform: String,
  matchType: String,
  map: [Object, String],
  maps: Array,
  horizontal: Boolean,
});

defineEmits(['update:platform', 'update:matchType', 'update:map']);

const platformOptions = computed(() => [
  { value: '', label: t('coach.filters.all_platforms') },
  { value: 'Valve', label: 'Valve' },
  { value: 'Faceit', label: 'Faceit' },
  { value: 'Renown', label: 'Renown' },
  { value: 'gamers_club', label: 'Gamers Club' },
]);

const platformLabel = computed(() => platformFilterDisplayLabel(props.platform));

const matchTypes = computed(() => {
  const typeMap = {
    Valve: ['Competitive', 'Premier', 'Wingman'],
    Faceit: ['Matchmaking', 'Championship'],
    Renown: ['Matchmaking'],
    gamers_club: ['Competitive'],
  };
  return typeMap[props.platform] || [];
});
</script>
